import { Column, ColumnDef, Row, RowModel, Table } from "@tanstack/react-table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../button";
import { cn } from "../../../utils/cn";
import {
  BADGE_PRINT_STATUSES,
  PAGE_SIZE,
  STATUS_INDICATOR_COLUMN,
  SYSTEM_DETAIL_CODES,
  TABLE_IDS,
  USER_ROLES_IDS,
  VISITOR_VISIT_STATUSES,
  VisitorDetails,
} from "../../../constants/common";
import {
  formattedDate,
  formattedDateTimeWithoutSec,
  mapToOptions,
} from "../../../lib/functions";
import { ArrowUpDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../routes";
import TableWrapper from "../TableWrapper";
import useAuthStore from "../../../store/Auth";
import { useGetPendingApprovedVisitors } from "../../../hooks/dashboard/query";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import PrintBadgeModal from "../../modal/PrintBadgeModal";
import {
  useGetBadgePrintInfoById,
  useGetBulkBadgePrintInfoById,
} from "../../../hooks/visitor/mutation";
import BulkPrintBadgeModal from "../../modal/BulkPrintBadgeModal";
import SelectCheckInCheckoutModal from "../../modal/SelectCheckInCheckoutModal";
import { Controller, useForm } from "react-hook-form";
import ComboboxInput from "../../inputs/ComboboxInput";
import { useGetAllSystemDetailCodesDropdown } from "../../../hooks/common/query";

const ExpectedVisitorsTable = ({
  visitDurationType: externalVisitDurationType,
}: {
  visitDurationType?: number;
}) => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  const [isPrintBadgeModalOpen, setIsPrintBadgeModalOpen] = useState(false);
  const [isBulkPrintBadgeModalOpen, setIsBulkPrintBadgeModalOpen] =
    useState(false);

  const [selectedAppointment, setSelectedAppointment] = useState({});
  const [selectedAppointments, setSelectedAppointments] = useState<any>([]);

  const [selectedAppointmentIds, setSelectedAppointmentIds] = useState<
    string[]
  >([]);

  const { data: allVisitDurations } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.VISIT_DURATION_TYPES,
  });

  const visitDurations = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allVisitDurations),
    [allVisitDurations],
  );

  const { control, watch, setValue } = useForm({
    defaultValues: {
      visitDurationType: externalVisitDurationType || 0,
    },
  });

  const visitDurationType = watch("visitDurationType");

  // Update form value when external prop changes
  useEffect(() => {
    if (externalVisitDurationType !== undefined) {
      setValue("visitDurationType", externalVisitDurationType);
    }
  }, [externalVisitDurationType, setValue]);

  const [
    isSelectCheckInCheckoutModalOpen,
    setIsSelectCheckInCheckoutModalOpen,
  ] = useState<boolean>(false);

  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);

  const [allVisitors, setAllVisitors] = useState([]);

  const { data: visitors } = useGetPendingApprovedVisitors({
    type: 1,
    pageNumber: page,
    pageSize: pageSize,
    search: search,
    encryptedLocationID:
      user?.n_RoleID !== USER_ROLES_IDS.ADMIN
        ? user?.n_LocationID?.toString()
          ? user?.n_LocationID?.toString()
          : ""
        : "",
    VisitDurationType: visitDurationType || 0,
    filterRoleID: 0,
  });

  const { mutateAsync: GetBadgePrintInfoById } = useGetBadgePrintInfoById();
  const { mutateAsync: GetBulkBadgePrintInfoById } =
    useGetBulkBadgePrintInfoById();

  useEffect(() => {
    if (visitors) {
      setAllVisitors(visitors.data || []);
      setPages(visitors.pagination?.pages || 1);
      setCount(visitors.pagination?.count || 0);
    } else {
      setAllVisitors([]);
    }
  }, [visitors, setPages]);

  const onCheckIn = useCallback(
    (appointmentId: string) => {
      navigate(`${APP_ROUTES.CHECK_IN}?ID=${appointmentId?.toString()}`);
    },
    [navigate],
  );

  const onPrint = useCallback(
    async (appointmentId: string) => {
      try {
        const body = {
          encryptedId: appointmentId ? appointmentId?.toString() : "",
        };

        const res = await GetBadgePrintInfoById(body);

        setSelectedAppointment(res);
        setIsPrintBadgeModalOpen(true);
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [GetBadgePrintInfoById, t],
  );

  const onPrintAll = useCallback(async () => {
    if (selectedAppointmentIds?.length === 0) {
      toast.error("Kindly select appointment(s) to print!");
      return;
    }
    try {
      const body = selectedAppointmentIds.map((item) => ({
        encryptedId: item,
      }));

      const res = await GetBulkBadgePrintInfoById(body);

      setSelectedAppointments(res);
      setIsBulkPrintBadgeModalOpen(true);
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    }
  }, [GetBulkBadgePrintInfoById, selectedAppointmentIds, t]);

  const onRowSelection = useCallback(
    (
      selectedRowsModel: RowModel<{
        [key: string]: any;
      }>,
    ) => {
      setSelectedAppointmentIds(Object.keys(selectedRowsModel?.rowsById));
    },
    [],
  );

  const columns: ColumnDef<VisitorDetails>[] = useMemo(
    () =>
      [
        {
          accessorKey: "select",
          header: ({ table }: { table: Table<VisitorDetails> }) => (
            <div className="px-4 text-center">
              <input
                type="checkbox"
                checked={table.getIsAllRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
                className="cursor-pointer"
              />
            </div>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => (
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              className="cursor-pointer"
            />
          ),
        },
        {
          accessorKey: "s_AppointmentCode",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("referenceNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span
                onClick={() => {
                  if (
                    user?.n_RoleID === USER_ROLES_IDS.ADMIN ||
                    user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER ||
                    user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
                    user?.n_RoleID === USER_ROLES_IDS.RECEPTION
                  ) {
                    if (
                      user?.n_RoleID === USER_ROLES_IDS.RECEPTION ||
                      user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER
                    ) {
                      navigate(
                        `${
                          APP_ROUTES.CHECK_IN
                        }?ID=${row?.original?.n_AppointmentID.toString()}`,
                      );
                    } else {
                      navigate(
                        `${
                          APP_ROUTES.APPROVE_REQUEST
                        }?ID=${row?.original?.n_AppointmentID.toString()}`,
                      );
                    }
                  }
                }}
                className={cn(
                  (user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
                    user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER ||
                    user?.n_RoleID === USER_ROLES_IDS.ADMIN ||
                    user?.n_RoleID === USER_ROLES_IDS.RECEPTION) &&
                    "text-blue-800 underline cursor-pointer capitalize",
                )}
              >
                {row.original.s_AppointmentCode}
              </span>
            );
          },
        },
        {
          accessorKey: "s_ApplyFor_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("applyFor")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_ApplyFor_En
                  : row.original.s_ApplyFor_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisitorName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("name")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span className="capitalize">{row.original.s_VisitorName}</span>
            );
          },
        },
        {
          accessorKey: "s_Sponsor",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("companyName")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Sponsor}</span>;
          },
        },
        {
          accessorKey: "dt_AppointmentStartTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitStartDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(
                  row.original.dt_AppointmentStartTime,
                )}
              </span>
            );
          },
        },
        {
          accessorKey: "dt_AppointmentEndTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitEndDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(
                  row.original.dt_AppointmentEndTime,
                )}
              </span>
            );
          },
        },
        {
          accessorKey: "s_MobileNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("mobileNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_MobileNo}</span>;
          },
        },
        {
          accessorKey: "s_Email",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("email")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Email}</span>;
          },
        },
        {
          accessorKey: "dt_DateOfBirth",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("dateOfBirth")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_DateOfBirth
                  ? formattedDate(new Date(row.original.dt_DateOfBirth), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_Nationality_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationality")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_Nationality_En
                  : row.original.s_Nationality_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_HostName",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostName")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_HostName}</span>;
          },
        },
        {
          accessorKey: "s_HostEmail",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostEmail")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_HostEmail}</span>;
          },
        },
        {
          accessorKey: "s_LocationName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("location")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_LocationName_En
                  : row.original.s_LocationName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_BuildingName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("building")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_BuildingName_En
                  : row.original.s_BuildingName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_GateName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("gate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_GateName_En
                  : row.original.s_GateName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_MeetingRoomName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("meetingRoom")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_MeetingRoomName_En
                  : row.original.s_MeetingRoomName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_DepartmentName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("department")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_DepartmentName_En
                  : row.original.s_DepartmentName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisitType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_VisitType_En
                  : row.original.s_VisitType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_PurposeEn",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitPurpose")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_PurposeEn
                  : row.original.s_PurposeAr}
              </span>
            );
          },
        },
        {
          accessorKey: "s_DocumentType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("documentType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_DocumentType_En
                  : row.original.s_DocumentType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_Iqama",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("iqama")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Iqama}</span>;
          },
        },
        {
          accessorKey: "dt_IqamaExpiry",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("iqamaExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_IqamaExpiry
                  ? formattedDate(new Date(row.original.dt_IqamaExpiry), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "eidNumber",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationalID")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.eidNumber}</span>;
          },
        },
        {
          accessorKey: "dt_EIDExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationalIDExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_EIDExpiryDate
                  ? formattedDate(new Date(row.original.dt_EIDExpiryDate), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "passportNumber",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("passportNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.passportNumber}</span>;
          },
        },
        {
          accessorKey: "dt_PassportExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("passportExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_PassportExpiryDate
                  ? formattedDate(
                      new Date(row.original.dt_PassportExpiryDate),
                      true,
                    )
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisaNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visaNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_VisaNo}</span>;
          },
        },
        {
          accessorKey: "dt_VisaExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visaExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_VisaExpiryDate
                  ? formattedDate(
                      new Date(row.original.dt_VisaExpiryDate),
                      true,
                    )
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VehicleNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("vehicleNo")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_VehicleNo || "-"}</span>;
          },
        },
        {
          accessorKey: "s_IsVehicleAllowed_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("vehiclePermit")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <div
                className={cn(
                  "text-center rounded-lg py-0.5 mx-auto max-w-[128px] w-[128px]",
                  row.original.s_VehicleNo
                    ? row.original.n_IsVehicleAllowed === -1
                      ? "bg-yellow-500"
                      : row.original.n_IsVehicleAllowed === 1
                        ? "bg-green-600"
                        : "bg-red-500"
                    : "bg-transparent",
                )}
              >
                <p
                  className={cn(
                    "text-sm whitespace-normal",
                    row.original.s_VehicleNo ? "text-white" : "text-[#828282]",
                  )}
                >
                  {row.original.s_VehicleNo
                    ? i18n.language === "en"
                      ? row.original.s_IsVehicleAllowed_En
                      : row.original.s_IsVehicleAllowed_Ar
                    : "-"}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "s_CreatedBy",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("createdBy")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_CreatedBy}</span>;
          },
        },
        {
          accessorKey: "dt_CreatedDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("createdDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(row.original.dt_CreatedDate)}
              </span>
            );
          },
        },
        {
          accessorKey: "n_BadgePrintStatus",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("printStatus")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <p
                className={cn(
                  "text-center font-semibold",
                  row.original.n_BadgePrintStatus ===
                    BADGE_PRINT_STATUSES.PRINTED
                    ? "text-green-600"
                    : "text-red-500",
                )}
              >
                {i18n.language === "en"
                  ? row?.original.s_BadgePrintStatus_En
                  : row?.original.s_BadgePrintStatus_Ar}
              </p>
            );
          },
        },
        {
          accessorKey: "s_CheckedInStatus_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("status")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <div>
                <p
                  className={cn(
                    "text-center font-semibold capitalize",
                    row.original.n_CheckedInStatus ===
                      VISITOR_VISIT_STATUSES.VISIT_NOT_STARTED
                      ? "text-yellow-500"
                      : row.original.n_CheckedInStatus ===
                          VISITOR_VISIT_STATUSES.CHECKED_IN
                        ? "text-green-600"
                        : "text-purple-tertiary",
                  )}
                >
                  {i18n.language === "en"
                    ? row?.original.s_CheckedInStatus_En
                    : row?.original.s_CheckedInStatus_Ar}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "action",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("action")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <div className="flex items-center gap-1">
                <Button
                  onClick={() => onPrint(row.original.n_AppointmentID)}
                  className="bg-green-600 py-0.5 w-[80px] hover:bg-green-700"
                >
                  {t("print")}
                </Button>

                <Button
                  onClick={() => onCheckIn(row.original.n_AppointmentID)}
                  disabled={
                    row.original.n_CheckedInStatus ===
                    VISITOR_VISIT_STATUSES.CHECKED_IN
                  }
                  className="py-0.5 min-w-[80px]"
                >
                  {t("checkIn")}
                </Button>
              </div>
            );
          },
        },
      ].filter((column) => {
        let excludeKeys = [];

        if (
          user?.n_RoleID !== USER_ROLES_IDS.RECEPTION &&
          user?.n_RoleID !== USER_ROLES_IDS.SECURITY_MANAGER
        ) {
          excludeKeys.push(
            "select",
            "n_BadgePrintStatus",
            "action",
            "s_CheckedInStatus_En",
            // "currentRoleAppointmentStatusEN"
          );
        }

        if (
          user?.n_RoleID === USER_ROLES_IDS.ADMIN ||
          user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER
        ) {
          excludeKeys.push(
            // "currentRoleAppointmentStatusEN",
            STATUS_INDICATOR_COLUMN.CURRENT_ROLE_STATUS,
          );
        } else {
          excludeKeys.push(STATUS_INDICATOR_COLUMN.ADMIN_STATUS);
        }

        // return !excludeKeys.includes(column.accessorKey);
        return column.accessorKey
          ? !excludeKeys.includes(column.accessorKey)
          : true;
      }),
    [i18n.language, navigate, onCheckIn, onPrint, t, user?.n_RoleID],
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allVisitors}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.EXPECTED_VISITORS}
        isHideStatusIndicator
        hideColumns={{
          s_ApplyFor_En: false,
          s_HostEmail: false,
          eidNumber: false,
          dt_EIDExpiryDate: false,
          passportNumber: false,
          dt_PassportExpiryDate: false,
          s_VisaNo: false,
          dt_VisaExpiryDate: false,
          s_CreatedBy: false,
          dt_CreatedDate: false,
        }}
        rowSelectionId="n_AppointmentID"
        onRowSelection={onRowSelection}
        ButtonsWrapper={
          <>
            <Controller
              name="visitDurationType"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={visitDurations}
                  value={
                    visitDurations?.find((item) => item.value === value)
                      ?.labelEn || ""
                  }
                  onChange={onChange}
                  styles="py-2 mb-1.5 rounded-lg ring-1 ring-purple-primary dark:ring-white focus:outline-none focus:ring-1"
                />
              )}
            />
            {(user?.n_RoleID === USER_ROLES_IDS.RECEPTION ||
              user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER) && (
              <>
                <Button
                  onClick={() => setIsSelectCheckInCheckoutModalOpen(true)}
                  className="dark:ring-1 dark:ring-white dark:bg-transparent"
                >
                  {t("scanQRCode")}
                </Button>
                <Button
                  disabled={selectedAppointmentIds?.length === 0}
                  onClick={onPrintAll}
                  className="dark:ring-1 dark:ring-white dark:bg-transparent"
                >
                  {t("bulkPrint")}
                </Button>
              </>
            )}
          </>
        }
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <PrintBadgeModal
        appointment={selectedAppointment}
        isOpen={isPrintBadgeModalOpen}
        onOpenChange={() => setIsPrintBadgeModalOpen(false)}
      />

      <BulkPrintBadgeModal
        appointments={selectedAppointments}
        isOpen={isBulkPrintBadgeModalOpen}
        onOpenChange={() => setIsBulkPrintBadgeModalOpen(false)}
      />

      <SelectCheckInCheckoutModal
        isOpen={isSelectCheckInCheckoutModalOpen}
        onOpenChange={() => setIsSelectCheckInCheckoutModalOpen(false)}
      />
    </>
  );
};

export default React.memo(ExpectedVisitorsTable);
